import React, { useState } from "react";

export default function BulkLootspotEditor({ markerNames, markers = [], onApply, onClose }) {
  const [input, setInput] = useState("");
  const [finalLobbyInput, setFinalLobbyInput] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Helper functions for localStorage
  const LOOTSPOT_KEY = "bulk_lootspot_last_input";
  const FINALLOBBY_KEY = "bulk_lootspot_last_final_lobby";

  // Save to localStorage on change
  React.useEffect(() => {
    if (input) localStorage.setItem(LOOTSPOT_KEY, input);
  }, [input]);
  React.useEffect(() => {
    if (finalLobbyInput) localStorage.setItem(FINALLOBBY_KEY, finalLobbyInput);
  }, [finalLobbyInput]);

  // Load from localStorage
  function loadLastLootspots() {
    const val = localStorage.getItem(LOOTSPOT_KEY);
    if (val) setInput(val);
  }
  function loadLastFinalLobby() {
    const val = localStorage.getItem(FINALLOBBY_KEY);
    if (val) setFinalLobbyInput(val);
  }

  // Global flag folder selection
  const FLAG_FOLDER_KEY = "lootspot_flag_folder";
  const [flagFolder, setFlagFolder] = useState(() => {
    return localStorage.getItem(FLAG_FOLDER_KEY) || "VP-Flags";
  });
  React.useEffect(() => {
    localStorage.setItem(FLAG_FOLDER_KEY, flagFolder);
  }, [flagFolder]);

  // Helper to get available flags from selected folder
  function getAvailableFlags() {
    // Based on actual files in the folders
    if (flagFolder === "Kowo-Flags") {
      return [
        "",
        "1-CAN.png","2-POL.png","3-FIN.png","4-DEN.png","5-BRA.png","6-GER.png","7-FRA.png","8-UK.png",
        "9-SWE.png","10-NOR.png","11-SPA.png","12-AU.png","13-TR.png","14-USA.png","15-POR.png",
        "16-ARG.png","17-KOR.png","18-JP.png","19-AUS.png","20-CN.png","21-CRO.png","22-GR.png","23-UA.png"
      ];
    } else {
      // VP-Flags
      return [
        "",
        "1-CAN.png","2-ITA.png","3-FIN.png","4-IND.png","5-BRA.png","6-GER.png","7-FRA.png","8-GBR.png",
        "9-SWE.png","10-JAM.png","11-ESP.png","12-AUT.png","13-TUR.png","14-USA.png","15-POR.png",
        "16-ARG.png","17-KOR.png","18-JPN.png","19-AUS.png","20-CHN.png","21-CRO.png","22-GRE.png",
        // Additional non-numbered flags could be mapped to higher slots if needed
        // "23-DEN.png","24-NOR.png","25-POL.png","26-UKR.png"
      ];
    }
  }

  // Helper to get full flag path
  function getFlagPath(flagFile) {
    if (!flagFile) return "";
    return `/${flagFolder}/${flagFile}`;
  }

  // Helper: parse lootspot input (teams, tiers, contests)
  function parseLootspotInput() {
    const lines = input
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const teams = [];
    let current = null;
    lines.forEach((line) => {
      if (!line) return;
      // NA scrims format: @TAG | Team Name
      if (/^@/.test(line)) {
        if (line.includes("|")) {
          // @TAG | Team Name
          const parts = line.split("|").map((s) => s.trim());
          const tag = parts[0].replace(/^@/, "").trim();
          const name = parts[1] || tag;
          if (current) teams.push(current);
          let teamName = name;
          if (name.length > 12 && tag.length > 0) teamName = tag;
          current = { team: teamName, tier: "", spots: {} };
        } else {
          // Ako je linija tipa @Ime MIX__ ili @Ime MIX, tretiraj kao novi tim
          const lineNoAt = line.replace(/^@/, "").trim();
          // Ako ima više reči (npr. vazo MIX__), tretiraj kao novi tim
          if (lineNoAt.split(/\s+/).length > 1) {
            if (current) teams.push(current);
            current = { team: lineNoAt, tier: "", spots: {} };
          } else {
            // @Tournament (tier)
            if (current) current.tier = lineNoAt;
          }
        }
      } else if (/^Tier:/i.test(line)) {
        if (current) current.tier = line.replace(/^Tier:/i, "").trim();
      } else if (/^[EMTR]:/i.test(line)) {
        if (current) {
          const mapKey =
            line[0].toUpperCase() === "E"
              ? "erangel"
              : line[0].toUpperCase() === "M"
                ? "miramar"
                : line[0].toUpperCase() === "T"
                  ? "taego"
                  : line[0].toUpperCase() === "R"
                    ? "rondo"
                    : null;
          if (mapKey) {
            current.spots[mapKey] = line.slice(2).trim();
          }
        }
      } else {
        // Stari format: nova ekipa
        if (current) teams.push(current);
        current = { team: line, tier: "", spots: {} };
      }
    });
    if (current) teams.push(current);
    // Build updates for team/tier
    const updates = {};
    const notFound = [];
    teams.forEach((team) => {
      Object.entries(team.spots).forEach(([map, markerName]) => {
        // Pronađi markerId po markerName iz markerNames
        const markerNamesForMap = markerNames[map] || {};
        const markerId = Object.keys(markerNamesForMap).find(
          (id) => markerNamesForMap[id].toLowerCase() === markerName.trim().toLowerCase(),
        );
        if (markerId) {
          updates[markerId] = { team: team.team, tier: team.tier, map: map };
        } else {
          notFound.push(`${markerName} (${map})`);
        }
      });
    });
    // Detect contests and create contest markers
    const contestUpdates = {};
    const markerTeams = {};
    teams.forEach((team) => {
      Object.entries(team.spots).forEach(([map, markerName]) => {
        const markerNamesForMap = markerNames[map] || {};
        const markerId = Object.keys(markerNamesForMap).find(
          (id) => markerNamesForMap[id].toLowerCase() === markerName.trim().toLowerCase(),
        );
        if (markerId) {
          if (!markerTeams[markerId]) markerTeams[markerId] = [];
          markerTeams[markerId].push({ team: team.team, tier: team.tier, map });
        }
      });
    });
    // Create contest markers (only for two teams)
    const offset = 0.035; // reduced for closer marker placement
    Object.entries(markerTeams).forEach(([markerId, teamObjs]) => {
      if (teamObjs.length === 2) {
        // Find original marker coordinates
        const origMarker = markers.find((m) => m.id === markerId);
        const origX = origMarker?.x ?? 0.5;
        const origY = origMarker?.y ?? 0.5;
        // Shift original marker left from original position
        updates[markerId] = {
          ...updates[markerId],
          team: teamObjs[0].team,
          tier: teamObjs[0].tier,
          x_offset: 0,
          x: origX - offset,
          y: origY,
          map: teamObjs[0].map,
        };
        // Create contest marker right from original position
        const contestMarkerId = `${markerId}_contest1`;
        contestUpdates[contestMarkerId] = {
          team: teamObjs[1].team,
          tier: teamObjs[1].tier,
          x_offset: 0,
          x: origX + offset,
          y: origY,
          map: teamObjs[1].map,
        };
      }
    });
    // Merge contest updates
    const finalUpdates = { ...updates, ...contestUpdates };
    return { teams, updates: finalUpdates, notFound };
  }

  // Handler: samo lootspotovi i timovi, BEZ zastava
  function handleApplyBulkLootspot() {
    const { teams, updates, notFound } = parseLootspotInput();
    if (Object.keys(updates).length === 0) {
      setError("No lootspots found!");
      return;
    }
    setError(notFound.length ? "No lootspots found: " + notFound.join(", ") : "");
    setResult({ teams, updates, notFound });
    if (onApply) onApply(updates);
  }

  // Handler: lootspotovi, timovi, i zastave SAMO ako ima lobby data
  function handleApplyWithFlags() {
    const { teams, updates, notFound } = parseLootspotInput();
    let finalUpdates = { ...updates };
    // --- AUTOMATSKO POSTAVLJANJE FLAG-A NA MARKER ---
    // Parse final lobby input za slotove
    const linesFlag = finalLobbyInput
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
  const slotToTeam = {};
    linesFlag.forEach((line) => {
      const match = line.match(/^Slot (\d+):\s*([^,]+)/);
      if (match) {
        const slot = match[1];
        const teamName = match[2].replace(/@/g, "").trim();
        slotToTeam[slot] = teamName;
      }
    });
    // Pripremi listu dostupnih flag fajlova
    let availableFlags = [];
    try {
      availableFlags = getAvailableFlags();
    } catch {}
    // Helper za normalizaciju imena
    const norm = (s) =>
      s
        .replace(/@/g, "")
        .replace(/\s+mix$/i, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

    // Samo ako ima lobby data, postavi zastave
    if (Object.keys(slotToTeam).length > 0) {
      // First, we need to get ALL markers from the parent component, not just the ones in finalUpdates
      // Since we can't access all markers here, we'll need to find teams that need flags assigned
      
      // Create a map of normalized team names to their flag assignments
      const teamFlagMap = {};
      
      // Pronađi sve timove iz lootspot podataka koji nisu u final lobby slotovima
      const allTeamsFromLootspot = Object.values(finalUpdates)
        .map((m) => m.team)
        .filter(Boolean)
        .map(norm);
      const allTeamsFromLobby = Object.values(slotToTeam).map(norm);
      const missingTeams = Array.from(
        new Set(allTeamsFromLootspot.filter((t) => t && !allTeamsFromLobby.includes(t))),
      );
      // Pronađi najveći slot broj iz SAMO originalnog final lobbyja (regex Slot X:)
      const originalLobbyLines = finalLobbyInput
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      const originalUsedSlots = [];
      originalLobbyLines.forEach((line) => {
        const match = line.match(/Slot\s*(\d+):/i);
        if (match) originalUsedSlots.push(Number(match[1]));
      });
      const maxSlot = originalUsedSlots.length ? Math.max(...originalUsedSlots) : 0;
      let slotOffset = 1;
      missingTeams.forEach((missingTeam) => {
        const slotNum = maxSlot + slotOffset;
        const flagFile = availableFlags.find((f) => f.split("-")[0] === String(slotNum));
        
        // Apply flag to ALL markers of this team, not just the first one
        Object.entries(finalUpdates).forEach(([id, marker]) => {
          if (norm(marker.team) === missingTeam) {
            if (flagFile) {
              finalUpdates[id] = { ...marker, flag: getFlagPath(flagFile), slot: slotNum };
            } else {
              finalUpdates[id] = { ...marker, flag: "", slot: slotNum };
            }
          }
        });
        slotOffset++;
      });
      // Dodeli flag markerima na osnovu slot broja (postojeći slotovi)
      Object.entries(slotToTeam).forEach(([slot, teamName]) => {
        const normalizedTeam = norm(teamName);
        
        // Store the flag assignment for this team
        const flagFile = availableFlags.find((f) => f.startsWith(`${slot}-`));
        if (flagFile) {
          teamFlagMap[normalizedTeam] = getFlagPath(flagFile);
        }
        
        // Apply to all markers that match this team
        Object.entries(finalUpdates).forEach(([id, marker]) => {
          const markerTeamNorm = norm(marker.team);
          // Check for partial matches to handle cases like "INS4NE_-_" matching "INS4NE- MIX"
          if (markerTeamNorm === normalizedTeam || 
              markerTeamNorm.includes(normalizedTeam) || 
              normalizedTeam.includes(markerTeamNorm.split(' ')[0])) {
            const next = { ...marker, slot: Number(slot) };
            if (flagFile) next.flag = getFlagPath(flagFile);
            finalUpdates[id] = next;
          }
        });
      });
    } else {
      // Ako nema lobby data, makni sve flagove
      Object.entries(finalUpdates).forEach(([id, marker]) => {
        finalUpdates[id] = { ...marker, flag: "", slot: "" };
      });
    }
    if (Object.keys(finalUpdates).length === 0) {
      setError("No lootspots found!");
      return;
    }
    setError(notFound.length ? "No lootspots found: " + notFound.join(", ") : "");
    setResult({ teams, updates: finalUpdates, notFound });
    if (onApply) onApply(finalUpdates);
  }

  // Klik na bluranu pozadinu zatvara modal
  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) {
      if (onClose) onClose();
    }
  }

  return (
    <div className="animate-fadein fixed inset-0 z-50 flex items-center justify-center bg-ewave-dark/50 backdrop-blur-md" onClick={handleBackdropClick}>
      <div className="relative w-1/5 rounded-md border-2 border-ewave-border bg-ewave-dark p-8 text-center">
        <h2 className="mb-2 text-2xl font-bold uppercase text-white">
          Bulk Lootspot Editor <span className="text-sm text-primary">Beta</span>
        </h2>
        <span className="mb-2 block font-semibold uppercase tracking-wide text-primary">
          Lootspot Data
        </span>
        <textarea
          className="w-full resize-y rounded-xl border-2 border-ewave-border bg-ewave-darker p-4 text-sm text-tertiary outline-none"
          rows={10}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`eWave.gg\nTier: 2\nE: Shelter\nM: SW of Pecado\nT: Song Am\nR: Bamboo`}
        />
        <div className="gap-2">
          <button
            type="button"
            onClick={loadLastLootspots}
            className="mb-0 mt-2 w-full rounded border border-ewave-border bg-ewave-darker px-4 py-2 font-semibold text-white hover:bg-ewave-hover"
          >
            Load last lootspots
          </button>
          <button
            type="button"
            className="mb-0 mt-2 w-full rounded border border-ewave-border bg-ewave-darker px-4 py-2 font-semibold text-white hover:bg-ewave-hover"
            onClick={handleApplyBulkLootspot}
          >
            Apply Bulk Lootspot
          </button>
        </div>
        <div className="my-4 h-px w-full bg-ewave-border"></div>
        <div className="mb-2 mt-3 flex flex-col items-center">
          <h2 className="mb-2 block font-semibold uppercase tracking-wide text-primary">
            Final Lobby Data
          </h2>
          <span className="text-sm text-tertiary" style={{ marginBottom: 8 }}>
            Bulk assignment currently works only with VP and Kowo killfeed flags.
          </span>
          <span className="text-sm text-tertiary">
            <a
              className="font-semibold text-primary"
              href="/observer.zip"
              download
              style={{ textDecoration: "underline", cursor: "pointer" }}
            >
              Click here to download observer file with VP flags.
            </a>
          </span>
          {/* Moved flag folder selector here */}
          <div className="mt-4 mb-2 w-full">
            <label className="block mb-1 text-primary font-semibold">Flag Select:</label>
            <select
              className="w-full rounded border border-ewave-border bg-ewave-darker p-2 text-white"
              value={flagFolder}
              onChange={e => setFlagFolder(e.target.value)}
            >
              <option value="VP-Flags">VP-Flags</option>
              <option value="Kowo-Flags">Kowo-Flags</option>
            </select>
          </div>
        </div>
        <textarea
          className="w-full resize-y rounded-xl border-2 border-ewave-border bg-ewave-darker p-4 text-sm text-tertiary outline-none"
          rows={6}
          value={finalLobbyInput}
          onChange={(e) => setFinalLobbyInput(e.target.value)}
          placeholder={`Slot 1: @TeamA\nSlot 2: @TeamB\nSlot 3: @TeamC`}
        />
        <div className="gap-2">
          <button
            className="mb-0 mt-2 w-full rounded border border-ewave-border bg-ewave-darker px-4 py-2 font-semibold text-white hover:bg-ewave-hover"
            onClick={loadLastFinalLobby}
          >
            Load last team slots
          </button>
          <button
            className="mb-0 mt-2 w-full rounded border border-ewave-border bg-ewave-darker px-4 py-2 font-semibold text-white hover:bg-ewave-hover"
            onClick={handleApplyWithFlags}
          >
            Apply with Final Lobby Flags
          </button>
        </div>
        {error && (
          <div className="mt-4 rounded-xl border-2 border-ewave-border bg-primary p-4 text-sm text-white">
            <span className="text-sm text-white">
              <span className="font-semibold">Warning:</span> The following lootspots were not found
              and were skipped:
            </span>
            <br />
            <span className="text-sm text-white">{error.replace("No lootspots found: ", "")}</span>
          </div>
        )}

        {result && (
          <div
            style={{
              marginTop: 20,
              padding: 16,
              background: "rgba(224, 170, 255, 0.1)",
              border: "1px solid rgba(224, 170, 255, 0.3)",
              borderRadius: 12,
              fontSize: 12,
            }}
          >
            <div
              style={{
                fontWeight: 700,
                marginBottom: 8,
                color: "#e0aaff",
              }}
            >
              Preview:
            </div>
            <pre
              style={{
                margin: 0,
                fontFamily: "monospace",
                color: "#fff",
                lineHeight: 1.4,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {result && result.updates
                ? `Found ${result.updates.length} marker updates:\n\n` +
                  result.updates
                    .map((u) => `${u.markerName} → ${u.team}${u.tier ? ` (${u.tier})` : ""}`)
                    .join("\n")
                : JSON.stringify(result, null, 2)}
            </pre>
          </div>
        )}
        <button
          type="button"
          className="mt-2 w-full rounded border border-ewave-border bg-ewave-darker px-4 py-2 font-semibold text-white hover:bg-ewave-hover"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
}
